'use client'

import type { RealtimeConnection } from '@elevenlabs/client'
import { useCallback, useRef, useState } from 'react'

import { generateUUID } from '@/lib/utils'

export interface Word {
  word: string
  start: number
  end: number
  logprob?: number
}

export interface Transcript {
  id: string
  text: string
  words: Word[]
}

export type STTError = 'permission-denied' | 'device-not-found' | 'unknown'

// Keep voice trading in English until additional languages are ready for release.
const STT_LANGUAGE_CODE = 'en'

export function useSubscribeSTT() {
  const connectionRef = useRef<RealtimeConnection>(null)

  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<STTError | null>(null)
  const [partial, setParitial] = useState('')
  const [transcripts, setTranscripts] = useState<Transcript[]>([])
  const [words, setWords] = useState<Word[]>([])

  const start = useCallback(async () => {
    setError(null)

    try {
      // GET - Single Use Token
      const res = await fetch('/api/elevenlabs/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!res.ok) throw new Error(`STT token request failed (${res.status})`)
      const { token } = (await res.json()) as { token: string }

      // Loaded on first dictation, not with the page: the client pulls in livekit (~1 MB) and
      // this hook sits in the always-mounted Order Form prompt bar.
      const { CommitStrategy, RealtimeEvents, Scribe } = await import('@elevenlabs/client')

      // Make connection with Elevenlabs ai (microphone config goes into connect() directly)
      const connection = Scribe.connect({
        token,
        modelId: 'scribe_v2_realtime',
        languageCode: STT_LANGUAGE_CODE,
        commitStrategy: CommitStrategy.VAD,
        vadSilenceThresholdSecs: 1.5,
        vadThreshold: 0.5,
        minSpeechDurationMs: 100,
        minSilenceDurationMs: 100,
        includeTimestamps: true,
        microphone: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      })

      connectionRef.current = connection
      setIsConnected(true)

      connection.on(RealtimeEvents.PARTIAL_TRANSCRIPT, (data) => {
        setParitial(data.text)
      })

      connection.on(RealtimeEvents.COMMITTED_TRANSCRIPT_WITH_TIMESTAMPS, (data) => {
        const extracted: Word[] =
          data.words
            ?.filter((word) => word.type === 'word')
            .map((word) => ({
              word: word.text ?? '',
              start: word.start ?? 0,
              end: word.end ?? 0,
              confidence: word.logprob,
            })) || []

        const MAX_TRANSCRIPTS = 200
        const MAX_WORDS = 2000
        setParitial('')
        setTranscripts((prev) => {
          const next = [...prev, { id: generateUUID(), text: data.text, words: extracted }]
          return next.length > MAX_TRANSCRIPTS ? next.slice(next.length - MAX_TRANSCRIPTS) : next
        })
        setWords((prev) => {
          const next = [...prev, ...extracted]
          return next.length > MAX_WORDS ? next.slice(next.length - MAX_WORDS) : next
        })
      })

      connection.on(RealtimeEvents.CLOSE, () => setIsConnected(false))
    } catch (err) {
      setIsConnected(false)
      connectionRef.current = null

      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') setError('permission-denied')
        else if (err.name === 'NotFoundError') setError('device-not-found')
        else setError('unknown')
      } else {
        setError('unknown')
      }
    }
  }, [])

  const stop = useCallback(() => {
    try {
      connectionRef.current?.close()
    } catch {
      // Connection may already be closed or closing
    }
    connectionRef.current = null
    setIsConnected(false)
    setParitial('')
  }, [])

  return {
    start,
    stop,
    isConnected,
    error,
    partial,
    transcripts,
    words,
  }
}
